import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import {
  Button,
  Column,
  Columns,
  Div,
  Dropdown,
  DropdownMenu,
  Icon,
  IconText,
  P,
} from "trunx";

import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import config from "../../config";
import { useLocaleContext } from "../../contexts/LocaleContext";
import { useRootStore } from "../../models/Root";
import { SensorDisplayPrecip, SensorDisplayStream } from "../../models/SensorDisplay";

interface SelectSensorFilterProps {
  onSelect?: () => void;
}

const SelectSensorFilter = ({ onSelect }: SelectSensorFilterProps) => {
  const {
    dict: {
      intervals: { durationLabel, lastLabel, max },
      panel: {
        sensors: { data: dict },
      },
    },
  } = useLocaleContext();

  const {
    sensors: {
      display,
      clearDisplay,
      displayPrecipWithInterval,
      displayStreamWithPeriod,
    },
    isMobile,
  } = useRootStore();

  const [mobileSensorType, setMobileSensorType] = useState<
    "precip" | "stream"
  >(() => (SensorDisplayStream.is(display) ? "stream" : "precip"));

  interface PrecipCheckboxProps {
    interval: string;
  }
  const PrecipCheckbox = observer(({ interval }: PrecipCheckboxProps) => {
    const isSelected =
      SensorDisplayPrecip.is(display) && display.interval === interval;
    const select = () => {
      if (isMobile || !isSelected) {
        displayPrecipWithInterval(interval);
      } else {
        clearDisplay();
      }
      onSelect?.();
    };

    return (
      <li data-cy="sensor-display-option" onClick={select}>
        <IconText>
          <Icon bulma="is-left">
            <FontAwesomeIcon icon={isSelected ? faCheckSquare : faSquare} />
          </Icon>
          {durationLabel[interval]}
        </IconText>
      </li>
    );
  });

  interface StreamCheckboxProps {
    period: string;
  }
  const StreamCheckbox = observer(({ period }: StreamCheckboxProps) => {
    const isSelected =
      SensorDisplayStream.is(display) && display.period === period;
    const label =
      period === "last" ? lastLabel : `${durationLabel[period]} ${max}`;
    const select = () => {
      if (isMobile || !isSelected) {
        displayStreamWithPeriod(period);
      } else {
        clearDisplay();
      }
      onSelect?.();
    };

    return (
      <li data-cy="sensor-display-option" onClick={select}>
        <IconText>
          <Icon bulma="is-left">
            <FontAwesomeIcon icon={isSelected ? faCheckSquare : faSquare} />
          </Icon>
          {label}
        </IconText>
      </li>
    );
  });

  const PrecipOptions = ({ dropdown }: { dropdown: boolean }) => (
    <>
      {config.precipIntervals.base.map((interval) => (
        <PrecipCheckbox key={interval} interval={interval} />
      ))}
      {dropdown ? (
        <li>
          <Dropdown isHoverable>
            <div className="dropdown-trigger">
              <IconText>
                <Icon bulma="is-left">
                  <FontAwesomeIcon icon={faAngleDown} />
                </Icon>
                {dict.other}
              </IconText>
            </div>
            <DropdownMenu>
              <Div bulma="dropdown-content">
                <ul>
                  {config.precipIntervals.more.map((interval) => (
                    <PrecipCheckbox key={interval} interval={interval} />
                  ))}
                </ul>
              </Div>
            </DropdownMenu>
          </Dropdown>
        </li>
      ) : (
        config.precipIntervals.more.map((interval) => (
          <PrecipCheckbox key={interval} interval={interval} />
        ))
      )}
    </>
  );

  const StreamOptions = () => (
    <>
      <StreamCheckbox period="last" />
      <StreamCheckbox period="PT6H" />
      <StreamCheckbox period="PT12H" />
      <StreamCheckbox period="P1D" />
    </>
  );

  if (isMobile) {
    return (
      <Div bulma="menu" className="sensor-select-menu">
        <div
          className="buttons has-addons sensor-type-toggle"
          data-cy="sensor-type-toggle"
        >
          <Button
            className={mobileSensorType === "precip" ? "is-primary" : ""}
            aria-label={dict.precip}
            aria-pressed={mobileSensorType === "precip"}
            onClick={() => setMobileSensorType("precip")}
          >
            {dict.precipShort}
          </Button>
          <Button
            className={mobileSensorType === "stream" ? "is-primary" : ""}
            aria-label={dict.stream}
            aria-pressed={mobileSensorType === "stream"}
            onClick={() => setMobileSensorType("stream")}
          >
            {dict.streamShort}
          </Button>
        </div>
        <P bulma="menu-label" className="sensor-time-label">
          {mobileSensorType === "precip" ? dict.interval : dict.period}
        </P>
        <ul className="menu-list sensor-time-options">
          {mobileSensorType === "precip" ? (
            <PrecipOptions dropdown={false} />
          ) : (
            <StreamOptions />
          )}
        </ul>
      </Div>
    );
  }

  return (
    <Columns bulma="is-mobile">
      <Column id="precip-col" bulma="is-half">
        <Div bulma="menu">
          <P bulma="menu-label" className="sensor-precip-label">
            {dict.precip}
          </P>
          <ul className="menu-list">
            <PrecipOptions dropdown />
          </ul>
        </Div>
      </Column>
      <Column bulma="is-half">
        <Div bulma="menu">
          <P bulma="menu-label">{dict.stream}</P>
          <ul className="menu-list">
            <StreamOptions />
          </ul>
        </Div>
      </Column>
    </Columns>
  );
};

export default observer(SelectSensorFilter);
